import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalFooter } from '../Modal';
import { useToast } from '../Toast';
import { Icons } from '../Icons';
import { fmt, iso } from '../../utils/formatters';
import { computeAvailableQty } from '../../services/calculator';
import type { SwapModalProps, Coin, CrossChainSwapOptions } from '../../types';

// Available blockchain networks
const BLOCKCHAIN_NETWORKS = [
  { id: 'ethereum', name: 'Ethereum', symbol: 'ETH' },
  { id: 'bsc', name: 'BNB Smart Chain', symbol: 'BSC' },
  { id: 'polygon', name: 'Polygon', symbol: 'MATIC' },
  { id: 'arbitrum', name: 'Arbitrum', symbol: 'ARB' },
  { id: 'optimism', name: 'Optimism', symbol: 'OP' },
  { id: 'avalanche', name: 'Avalanche', symbol: 'AVAX' },
  { id: 'base', name: 'Base', symbol: 'BASE' },
  { id: 'fantom', name: 'Fantom', symbol: 'FTM' },
  { id: 'solana', name: 'Solana', symbol: 'SOL' },
  { id: 'cosmos', name: 'Cosmos', symbol: 'ATOM' },
];

// Available bridge protocols
const BRIDGE_PROTOCOLS = [
  { id: 'stargate', name: 'Stargate Finance' },
  { id: 'wormhole', name: 'Wormhole' },
  { id: 'layerzero', name: 'LayerZero' },
  { id: 'hop', name: 'Hop Protocol' },
  { id: 'across', name: 'Across Protocol' },
  { id: 'synapse', name: 'Synapse Protocol' },
  { id: 'multichain', name: 'Multichain' },
  { id: 'celer', name: 'Celer cBridge' },
  { id: 'portal', name: 'Portal Bridge' },
  { id: 'other', name: 'Autre' },
];

// Available swap platforms
const SWAP_PLATFORMS = [
  { id: 'uniswap', name: 'Uniswap' },
  { id: 'sushiswap', name: 'SushiSwap' },
  { id: '1inch', name: '1inch' },
  { id: 'pancakeswap', name: 'PancakeSwap' },
  { id: 'curve', name: 'Curve Finance' },
  { id: 'balancer', name: 'Balancer' },
  { id: 'dodo', name: 'DODO' },
  { id: 'paraswap', name: 'ParaSwap' },
  { id: 'kyberswap', name: 'KyberSwap' },
  { id: 'traderjoe', name: 'Trader Joe' },
  { id: 'cex', name: 'CEX (Exchange centralisé)' },
  { id: 'other', name: 'Autre' },
];

// Slippage presets
const SLIPPAGE_PRESETS = [0.1, 0.5, 1, 3];

/**
 * SwapModal - Token swap/conversion modal with cross-chain support
 *
 * Features:
 * - Autocomplete search for destination token
 * - Real-time rate calculation
 * - Fee support (percentage)
 * - Estimated proceeds preview
 * - Available quantity validation
 * - Cross-chain swap fields (expert mode only)
 */
export function SwapModal({
  isOpen,
  onClose,
  fromToken,
  prices,
  onSwap,
  coins = [],
  isExpertMode = false
}: SwapModalProps) {
  const { t } = useTranslation();

  // Basic swap fields
  const [amount, setAmount] = useState<string>('');
  const [toTokenId, setToTokenId] = useState<string>('');
  const [toTokenQuery, setToTokenQuery] = useState<string>('');
  const [suggestions, setSuggestions] = useState<Coin[]>([]);
  const [date, setDate] = useState<string>(iso(new Date()));
  const [fee, setFee] = useState<string>('');
  const [rate, setRate] = useState<string>('-');
  const [preview, setPreview] = useState<string>('-');

  // Cross-chain fields (expert mode)
  const [isCrossChain, setIsCrossChain] = useState(false);
  const [sourceChain, setSourceChain] = useState<string>('');
  const [destinationChain, setDestinationChain] = useState<string>('');
  const [bridgeProtocol, setBridgeProtocol] = useState<string>('');
  const [bridgeFee, setBridgeFee] = useState<string>('');
  const [bridgeTxHash, setBridgeTxHash] = useState<string>('');
  const [slippageTolerance, setSlippageTolerance] = useState<number>(0.5);
  const [customSlippage, setCustomSlippage] = useState<string>('');
  const [actualSlippage, setActualSlippage] = useState<string>('');
  const [swapPlatform, setSwapPlatform] = useState<string>('');
  const [gasFeeUsd, setGasFeeUsd] = useState<string>('');

  // V2.I.10 : moved from custom modal-overlay + useModalAccessibility hook
  // to <Modal> (BaseModal). Gains : focus trap, body scroll lock, restore
  // focus, portal render.
  const toast = useToast();

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) return;
    setAmount('');
    setToTokenId('');
    setToTokenQuery('');
    setDate(iso(new Date()));
    setFee('');
    setRate('-');
    setPreview('-');
    // Reset cross-chain fields
    setIsCrossChain(false);
    setSourceChain('');
    setDestinationChain('');
    setBridgeProtocol('');
    setBridgeFee('');
    setBridgeTxHash('');
    setSlippageTolerance(0.5);
    setCustomSlippage('');
    setActualSlippage('');
    setSwapPlatform('');
    setGasFeeUsd('');
  }, [isOpen]);

  // Autocomplete suggestions
  useEffect(() => {
    if (!toTokenQuery.trim() || !coins.length) {
      setSuggestions([]);
      return;
    }

    const q = toTokenQuery.toLowerCase();
    const matches = coins
      .filter(c =>
        (c.symbol?.toLowerCase().includes(q) || c.name?.toLowerCase().includes(q)) &&
        c.id !== fromToken?.id
      )
      .slice(0, 8);

    setSuggestions(matches);
  }, [toTokenQuery, coins, fromToken]);

  // Calculate rate and preview
  useEffect(() => {
    if (!fromToken || !toTokenId || !amount || !prices[fromToken.id] || !prices[toTokenId]) {
      setRate('-');
      setPreview('-');
      return;
    }

    const fromPrice = prices[fromToken.id]?.usd;
    const toPrice = prices[toTokenId]?.usd;
    const amt = parseFloat(amount) || 0;
    const feePct = Math.max(0, parseFloat(fee) || 0) / 100;
    const bridgeFeeParsed = parseFloat(bridgeFee) || 0;
    const gasFeeParsed = parseFloat(gasFeeUsd) || 0;

    if (!fromPrice || !toPrice || !amt) {
      setRate('-');
      setPreview('-');
      return;
    }

    const rateValue = fromPrice / toPrice;
    let proceedsUsd = amt * fromPrice * (1 - feePct);

    // Subtract bridge and gas fees if cross-chain
    if (isCrossChain) {
      proceedsUsd -= bridgeFeeParsed + gasFeeParsed;
    }

    const qtyOut = Math.max(0, proceedsUsd / toPrice);

    setRate(`1 → ${rateValue.toFixed(6)}`);
    setPreview(fmt(qtyOut, 8));
  }, [fromToken, toTokenId, amount, fee, prices, isCrossChain, bridgeFee, gasFeeUsd]);

  const handleSelect = (coin: Coin): void => {
    setToTokenId(coin.id);
    setToTokenQuery(coin.symbol.toUpperCase());
    setSuggestions([]);
  };

  const handleSlippagePreset = (value: number): void => {
    setSlippageTolerance(value);
    setCustomSlippage('');
  };

  const handleCustomSlippage = (value: string): void => {
    setCustomSlippage(value);
    const parsed = parseFloat(value);
    if (!isNaN(parsed) && parsed > 0) {
      setSlippageTolerance(parsed);
    }
  };

  const handleSave = (): void => {
    if (!fromToken || !toTokenId || !amount || !date) {
      toast(t('swap.allFieldsRequired', 'Tous les champs sont requis'), 'warning');
      return;
    }

    const amt = parseFloat(amount);
    if (!isFinite(amt) || amt <= 0) {
      toast(t('swap.invalidQuantity', 'Quantité invalide'), 'error');
      return;
    }

    const avail = computeAvailableQty(fromToken.positions || []);
    if (amt > avail) {
      toast(t('swap.quantityExceedsAvailable', `Quantité > disponible (${fmt(avail, 8)})`), 'error');
      return;
    }

    const fromPrice = prices[fromToken.id]?.usd;
    const toPrice = prices[toTokenId]?.usd;
    if (!fromPrice || !toPrice) {
      toast(t('swap.priceUnavailable', 'Prix indisponible. Rafraîchis les prix.'), 'warning');
      return;
    }

    const feePct = Math.max(0, parseFloat(fee) || 0) / 100;

    // Build cross-chain options if in expert mode and cross-chain is enabled
    let crossChainOptions: CrossChainSwapOptions | undefined;
    if (isExpertMode && isCrossChain) {
      crossChainOptions = {
        sourceChain: sourceChain || undefined,
        destinationChain: destinationChain || undefined,
        bridgeProtocol: bridgeProtocol || undefined,
        bridgeFee: parseFloat(bridgeFee) || undefined,
        bridgeTxHash: bridgeTxHash || undefined,
        slippageTolerance: slippageTolerance || undefined,
        actualSlippage: parseFloat(actualSlippage) || undefined,
        swapPlatform: swapPlatform || undefined,
        gasFeeUsd: parseFloat(gasFeeUsd) || undefined,
      };
    } else if (isExpertMode) {
      // Include swap platform and slippage even for same-chain swaps in expert mode
      crossChainOptions = {
        slippageTolerance: slippageTolerance || undefined,
        actualSlippage: parseFloat(actualSlippage) || undefined,
        swapPlatform: swapPlatform || undefined,
        gasFeeUsd: parseFloat(gasFeeUsd) || undefined,
      };
    }

    onSwap(fromToken.id, toTokenId, amt, feePct, date, crossChainOptions);
    onClose();
  };

  // Title — string only on BaseModal, so we encode the cross-chain badge
  // as part of the title text (the original markup spread it across a flex
  // div which BaseModal's <h2 id="modal-title"> can't host).
  const modalTitle = isCrossChain
    ? `🔁 ${t('swap.title', 'Swap Token')} · CROSS-CHAIN`
    : `🔁 ${t('swap.title', 'Swap Token')}`;

  const modalFooter = (
    <ModalFooter>
      <button className="btn-ghost" onClick={onClose}>
        {t('common.cancel', 'Annuler')}
      </button>
      <button onClick={handleSave} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        {isCrossChain ? <Icons.ArrowRightLeft size={16} /> : <Icons.Repeat size={16} />}
        {isCrossChain ? t('swap.bridgeSwap', 'Bridge & Swap') : t('swap.swap', 'Swap')}
      </button>
    </ModalFooter>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="lg"
      footer={modalFooter}
    >
        {/* From Token (read-only) */}
        <div className="form-group">
          <label>{t('swap.from', 'Depuis')}</label>
          <input
            value={`${fromToken?.displayName || ''} (${fromToken?.id || ''})`}
            readOnly
          />
        </div>

        {/* Amount to Swap */}
        <div className="form-group">
          <label>{t('swap.quantity', 'Quantité à swap')}</label>
          <input
            type="number"
            step="0.00000001"
            value={amount}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
            placeholder="0.00"
          />
          {fromToken && (
            <div className="form-help-text">
              {t('swap.available', 'Disponible')}: {fmt(computeAvailableQty(fromToken.positions || []), 8)}
            </div>
          )}
        </div>

        {/* To Token (autocomplete) */}
        <div className="form-group form-group-autocomplete">
          <label>{t('swap.to', 'Vers (token)')}</label>
          <input
            type="text"
            value={toTokenQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setToTokenQuery(e.target.value)}
            onFocus={() => {
              if (suggestions.length > 0) {
                setSuggestions(suggestions);
              }
            }}
            onBlur={() => {
              setTimeout(() => {
                setSuggestions([]);
              }, 200);
            }}
            placeholder={t('swap.searchPlaceholder', 'Rechercher...')}
          />

          {/* Autocomplete dropdown */}
          {suggestions.length > 0 && (
            <div
              className="autocomplete-list"
              onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => e.preventDefault()}
            >
              {suggestions.map(coin => (
                <div
                  key={coin.id}
                  className="autocomplete-item"
                  onClick={() => handleSelect(coin)}
                >
                  <div className="autocomplete-item-symbol">
                    {coin.symbol.toUpperCase()}
                  </div>
                  <div className="autocomplete-item-name">
                    {coin.name}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Date */}
        <div className="form-group">
          <label>{t('swap.date', 'Date')}</label>
          <input
            type="date"
            value={date}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDate(e.target.value)}
          />
        </div>

        {/* Fee */}
        <div className="form-group">
          <label>{t('swap.fee', 'Frais (%)')}</label>
          <input
            type="number"
            step="0.01"
            value={fee}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFee(e.target.value)}
            placeholder="0"
          />
        </div>

        {/* Expert Mode Section */}
        {isExpertMode && (
          <>
            {/* Cross-Chain Toggle */}
            <div style={{
              marginTop: '16px',
              padding: '12px',
              background: 'var(--bg-secondary)',
              borderRadius: '8px',
              border: '1px solid var(--border)',
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
              }}>
                <input
                  type="checkbox"
                  checked={isCrossChain}
                  onChange={(e) => setIsCrossChain(e.target.checked)}
                  style={{ width: '18px', height: '18px' }}
                />
                <Icons.ArrowRightLeft size={16} style={{ color: 'var(--accent)' }} />
                {t('swap.crossChain', 'Cross-Chain / Bridge')}
              </label>
            </div>

            {/* Cross-Chain Fields */}
            {isCrossChain && (
              <div style={{
                marginTop: '12px',
                padding: '16px',
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(99, 102, 241, 0.05) 100%)',
                borderRadius: '8px',
                border: '1px solid rgba(139, 92, 246, 0.2)',
              }}>
                <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '12px', color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icons.Globe size={14} />
                  {t('swap.crossChainDetails', 'Détails Cross-Chain')}
                </div>

                {/* Source & Destination Chain */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '12px' }}>{t('swap.sourceChain', 'Chaîne source')}</label>
                    <select
                      aria-label={t('swap.sourceChain', 'Chaîne source')}
                      value={sourceChain}
                      onChange={(e) => setSourceChain(e.target.value)}
                      style={{ fontSize: '13px' }}
                    >
                      <option value="">{t('swap.selectChain', 'Sélectionner...')}</option>
                      {BLOCKCHAIN_NETWORKS.map(chain => (
                        <option key={chain.id} value={chain.id}>{chain.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '12px' }}>{t('swap.destChain', 'Chaîne destination')}</label>
                    <select
                      aria-label={t('swap.destChain', 'Chaîne destination')}
                      value={destinationChain}
                      onChange={(e) => setDestinationChain(e.target.value)}
                      style={{ fontSize: '13px' }}
                    >
                      <option value="">{t('swap.selectChain', 'Sélectionner...')}</option>
                      {BLOCKCHAIN_NETWORKS.map(chain => (
                        <option key={chain.id} value={chain.id}>{chain.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Bridge Protocol */}
                <div className="form-group" style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '12px' }}>{t('swap.bridgeProtocol', 'Protocole de Bridge')}</label>
                  <select
                    aria-label={t('swap.bridgeProtocol', 'Protocole de Bridge')}
                    value={bridgeProtocol}
                    onChange={(e) => setBridgeProtocol(e.target.value)}
                    style={{ fontSize: '13px' }}
                  >
                    <option value="">{t('swap.selectBridge', 'Sélectionner...')}</option>
                    {BRIDGE_PROTOCOLS.map(bridge => (
                      <option key={bridge.id} value={bridge.id}>{bridge.name}</option>
                    ))}
                  </select>
                </div>

                {/* Bridge Fee & TX Hash */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginBottom: '12px' }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '12px' }}>{t('swap.bridgeFee', 'Frais bridge ($)')}</label>
                    <input
                      type="number"
                      step="0.01"
                      value={bridgeFee}
                      onChange={(e) => setBridgeFee(e.target.value)}
                      placeholder="0.00"
                      style={{ fontSize: '13px' }}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label style={{ fontSize: '12px' }}>{t('swap.bridgeTxHash', 'TX Hash Bridge')}</label>
                    <input
                      type="text"
                      value={bridgeTxHash}
                      onChange={(e) => setBridgeTxHash(e.target.value)}
                      placeholder="0x..."
                      style={{ fontSize: '13px', fontFamily: 'monospace' }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Swap Platform & Slippage (always visible in expert mode) */}
            <div style={{
              marginTop: '12px',
              padding: '16px',
              background: 'var(--bg-secondary)',
              borderRadius: '8px',
              border: '1px solid var(--border)',
            }}>
              <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '12px', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Icons.Settings size={14} />
                {t('swap.advancedSettings', 'Paramètres avancés')}
              </div>

              {/* Swap Platform */}
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px' }}>{t('swap.platform', 'Plateforme de swap')}</label>
                <select
                  aria-label={t('swap.platform', 'Plateforme de swap')}
                  value={swapPlatform}
                  onChange={(e) => setSwapPlatform(e.target.value)}
                  style={{ fontSize: '13px' }}
                >
                  <option value="">{t('swap.selectPlatform', 'Sélectionner...')}</option>
                  {SWAP_PLATFORMS.map(platform => (
                    <option key={platform.id} value={platform.id}>{platform.name}</option>
                  ))}
                </select>
              </div>

              {/* Slippage Tolerance */}
              <div className="form-group" style={{ marginBottom: '12px' }}>
                <label style={{ fontSize: '12px' }}>{t('swap.slippageTolerance', 'Tolérance slippage (%)')}</label>
                <div style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
                  {SLIPPAGE_PRESETS.map(preset => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => handleSlippagePreset(preset)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: slippageTolerance === preset && !customSlippage ? '2px solid var(--accent)' : '1px solid var(--border)',
                        background: slippageTolerance === preset && !customSlippage ? 'rgba(99, 102, 241, 0.1)' : 'var(--bg)',
                        color: 'var(--text)',
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      {preset}%
                    </button>
                  ))}
                  <input
                    type="number"
                    step="0.1"
                    value={customSlippage}
                    onChange={(e) => handleCustomSlippage(e.target.value)}
                    placeholder={t('swap.custom', 'Custom')}
                    style={{ width: '80px', fontSize: '12px', textAlign: 'center' }}
                  />
                </div>
                {slippageTolerance > 2 && (
                  <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Icons.AlertTriangle size={12} />
                    {t('swap.highSlippageWarning', 'Attention: slippage élevé')}
                  </div>
                )}
              </div>

              {/* Actual Slippage & Gas Fee */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: '12px' }}>{t('swap.actualSlippage', 'Slippage réel (%)')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={actualSlippage}
                    onChange={(e) => setActualSlippage(e.target.value)}
                    placeholder="0.00"
                    style={{ fontSize: '13px' }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ fontSize: '12px' }}>{t('swap.gasFee', 'Frais gas ($)')}</label>
                  <input
                    type="number"
                    step="0.01"
                    value={gasFeeUsd}
                    onChange={(e) => setGasFeeUsd(e.target.value)}
                    placeholder="0.00"
                    style={{ fontSize: '13px' }}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Preview */}
        <div className="swap-preview" style={{ marginTop: '16px' }}>
          <div>
            <div className="preview-label">{t('swap.estimatedRate', 'Taux estimé')}</div>
            <div className="preview-value">{rate}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="preview-label">{t('swap.estimatedReceived', 'Reçu estimé')}</div>
            <div className="preview-value">{preview}</div>
          </div>
        </div>

        {/* Total Fees Summary (expert mode) */}
        {isExpertMode && (parseFloat(fee) > 0 || parseFloat(bridgeFee) > 0 || parseFloat(gasFeeUsd) > 0) && (
          <div style={{
            marginTop: '12px',
            padding: '12px',
            background: 'rgba(239, 68, 68, 0.1)',
            borderRadius: '8px',
            fontSize: '12px',
          }}>
            <div style={{ fontWeight: 600, marginBottom: '6px', color: '#ef4444' }}>
              {t('swap.totalFees', 'Total des frais')}:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', color: 'var(--muted)' }}>
              {parseFloat(fee) > 0 && <div>• Frais swap: {fee}%</div>}
              {parseFloat(bridgeFee) > 0 && <div>• Frais bridge: ${bridgeFee}</div>}
              {parseFloat(gasFeeUsd) > 0 && <div>• Frais gas: ${gasFeeUsd}</div>}
            </div>
          </div>
        )}
    </Modal>
  );
}
